import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatCard } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  DatabasesUsage,
  MailboxUsage,
  QuotaAndGroupUsage,
  WebsiteGroupSnapshot,
  WebsiteUsage,
} from '../../../../shared/models/usage.models';
import { BytesToSizePipe } from '../../../../shared/pipes/bytes-to-size-pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { WebsocketService } from '../../../../core/services/websocket.service';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { SearchFieldComponent } from '../../../../shared/components/search-field/search-field.component';
import { MatButtonModule } from '@angular/material/button';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { MatTableModule } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Plan } from '../../../../shared/models/plans.models';

@Component({
  selector: 'app-disk-usage',
  imports: [
    MatCard,
    MatProgressSpinnerModule,
    BytesToSizePipe,
    MatTooltipModule,
    FontAwesomeModule,
    MatProgressBarModule,
    SearchFieldComponent,
    MatButtonModule,
    TableComponent,
    MatTableModule,
  ],
  providers: [BytesToSizePipe],
  templateUrl: './disk-usage.component.html',
  styleUrl: './disk-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiskUsageComponent implements OnInit, OnDestroy {
  private websocketService = inject(WebsocketService);

  private sizePipe = inject(BytesToSizePipe);

  usage = input<QuotaAndGroupUsage>();

  plan = input<Plan>();

  newUsages = signal<{
    files?: number;
    mailboxes?: number;
    databases?: number;
  }>({});

  websiteUsage = computed(() => this.usage()?.websiteGroupSnapshot?.websiteUsage ?? []);

  // calculatedFilesUsage = computed(() =>
  //   this.websiteUsage().reduce((total, website) => total + website.size, 0),
  // );

  calculatedMailboxesUsage = computed(() =>
    this.websiteUsage().reduce(
      (total, website) =>
        total +
        website.mailboxUsage.reduce((mailboxTotal, mailbox) => mailboxTotal + mailbox.blockUsed, 0),
      0,
    ),
  );

  calculatedDatabasesUsage = computed(() =>
    this.websiteUsage().reduce(
      (total, website) =>
        total +
        website.databasesUsage.reduce(
          (databaseTotal, database) => databaseTotal + database.size,
          0,
        ),
      0,
    ),
  );

  filesUsage = computed(() => this.newUsages().files ?? this.usage()?.quotaSnapshot.userOnlyUsed);

  mailboxesUsage = computed(() => this.newUsages().mailboxes ?? this.calculatedMailboxesUsage());

  databasesUsage = computed(() => this.newUsages().databases ?? this.calculatedDatabasesUsage());

  siteTotal = computed(() => this.usage()?.quotaSnapshot.blockUsed || 0 + this.databasesUsage());

  currentUsage = computed(() => this.usage()?.quotaSnapshot.blockUsed);

  bandwidthLimit = computed(() => (this.plan()?.bandwidth || 0) * 1024);

  bandwidthPercent = computed(() => {
    const usage = this.currentUsage();
    const limit = this.bandwidthLimit();

    if (usage == null || limit == null || limit <= 0) {
      return 0;
    }

    return Math.round((usage / limit) * 10000) / 100;
  });

  homeDirPercentage = computed(() => {
    const quota = this.usage()?.quotaSnapshot;

    if (!quota || quota.blockHard <= 0) {
      return 0;
    }

    return (quota.blockUsed / quota.blockHard) * 100;
  });

  siteTotalPercentage = computed(() => {
    const limit = this.usage()?.quotaSnapshot.blockHard;

    if (limit == null || limit <= 0) {
      return 0;
    }

    return (this.siteTotal() / limit) * 100;
  });

  lastUpdated = computed(() => {
    const date = this.usage()?.quotaSnapshot.date;

    return date ? moment(date * 1000).format('DD.MM.YYYY, HH:mm:ss') : 'N/A';
  });

  updateInterval = computed(() => {
    const interval = this.usage()?.quotaSnapshot.interval;
    return interval ? interval / 60 : 60;
  });

  syncDowntime = computed(() => this.usage()?.quotaSnapshot.interval);

  syncUsage = output();

  isDisabled = signal(false);

  destroyed$ = new Subject();

  collectingData = signal(false);

  filters = signal<{ page: number; itemsPerPage: number; domain?: string }>({
    page: 1,
    itemsPerPage: 10,
  });

  websitesData = computed(() => {
    const records = this.websiteUsage() ?? [];

    // Search
    const filteredRecords = this.filters().domain
      ? records.filter((record) =>
          record.websiteDomain.toLowerCase().includes(this.filters().domain!.toLowerCase()),
        )
      : records;

    // Pagination
    const start = (this.filters().page - 1) * this.filters().itemsPerPage;
    const end = start + this.filters().itemsPerPage;

    return filteredRecords.slice(start, end);
  });

  websitesColumns = [
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: WebsiteUsage) => `${element.websiteDomain}`,
    },
    {
      columnDef: 'files',
      header: 'Files',
      cell: (element: WebsiteUsage) => `${this.sizePipe.transform(element.size) || '-'}`,
    },
    {
      columnDef: 'mailboxes',
      header: 'Mailboxes',
      cell: (element: WebsiteUsage) =>
        `${this.sizePipe.transform(element.mailboxUsage.reduce((acc, curr) => acc + curr.blockUsed, 0)) || '-'}`,
    },
    {
      columnDef: 'databases',
      header: 'Databases',
      cell: (element: WebsiteUsage) =>
        `${this.sizePipe.transform(element.databasesUsage.reduce((acc, curr) => acc + curr.size, 0)) || '-'}`,
    },
    {
      columnDef: 'total',
      header: 'Total',
      cell: (e: WebsiteUsage) =>
        `${this.sizePipe.transform(
          e.size +
            e.mailboxUsage.reduce((acc, curr) => acc + curr.blockUsed, 0) +
            e.databasesUsage.reduce((acc, curr) => acc + curr.size, 0),
        )}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  expandedTable = signal<'mailboxes' | 'databases'>('databases');

  databasesColumns = [
    {
      columnDef: 'name',
      header: 'Database Name',
      cell: (element: DatabasesUsage) => `${element.databaseName}`,
    },
    {
      columnDef: 'size',
      header: 'Size',
      cell: (element: DatabasesUsage) => `${this.sizePipe.transform(element.size) || '-'}`,
    },
  ];

  mailboxesColumns = [
    {
      columnDef: 'email',
      header: 'Email',
      cell: (element: MailboxUsage) => `${element.mailboxAddress}`,
    },
    {
      columnDef: 'size',
      header: 'Size',
      cell: () => '',
    },
  ];

  ngOnInit(): void {
    const snapshot = this.usage()?.websiteGroupSnapshot;
    if (snapshot) {
      const remainingDowntime = (snapshot.date + snapshot.interval) * 1000 - Date.now();
      if (remainingDowntime > 0) {
        this.isDisabled.set(true);
        setTimeout(() => {
          this.isDisabled.set(false);
        }, remainingDowntime);
      }
    }

    this.websocketService
      .listen<{ payload: WebsiteGroupSnapshot }>('websiteGroupUsageUpdated')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        // const websiteUsage = data.payload.websiteUsage.find(
        //   (w) => w.websiteId === this.websiteData().website.id,
        // );
        // const newFilesUsage = websiteUsage?.size;
        // const newMailboxUsage = websiteUsage?.mailboxUsage.reduce(
        //   (acc, curr) => acc + curr.blockUsed,
        //   0,
        // );
        // const newDatabaseUsage = websiteUsage?.databasesUsage.reduce(
        //   (acc, curr) => acc + curr.size,
        //   0,
        // );
        // this.newUsages.set({
        //   files: newFilesUsage,
        //   mailboxes: newMailboxUsage,
        //   databases: newDatabaseUsage,
        // });
        this.collectingData.set(false);
        console.log(data);
      });
  }

  syncDiskUsage(): void {
    this.isDisabled.set(true);
    this.syncUsage.emit();

    const downtime = this.syncDowntime();
    if (downtime)
      setTimeout(() => {
        this.isDisabled.set(false);
      }, downtime * 1000);
  }

  collectData(): void {
    this.syncDiskUsage();
    this.collectingData.set(true);
  }

  handlePageEvent(event: PageEvent) {
    this.filters.update((f) => ({ ...f, page: event.pageIndex + 1 }));
  }

  onSearch(text: string) {
    this.filters.update((f) => ({ ...f, domain: text }));
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
